import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const sections = [
  {
    number: '1',
    title: 'AI-Generated Reports',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          Payments for AI-generated preliminary reports are generally non-refundable once a report
          has been generated and delivered.
        </p>
        <p>
          Because the system begins processing user-submitted materials immediately and
          computational resources are consumed during generation, refunds cannot typically be issued
          after a report is created.
        </p>
        <p>However, refunds may be granted if:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>the report fails to generate due to a system error</li>
          <li>the report cannot be accessed or downloaded due to a platform malfunction</li>
          <li>duplicate charges occur due to technical issues</li>
        </ul>
        <p>Requests for these refunds must be submitted within 7 days of the transaction.</p>
      </div>
    ),
  },
  {
    number: '2',
    title: 'Expert Review Services',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>If FireCaseIQ offers optional expert review services through independent experts:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Payments for expert work may be refundable before the expert begins work.</li>
          <li>
            Once an expert has started reviewing materials or preparing feedback, the payment
            becomes non-refundable.
          </li>
        </ul>
        <p>
          If an expert fails to deliver the agreed review within a reasonable timeframe, FireCaseIQ
          may, at its discretion:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>issue a refund, or</li>
          <li>assign a replacement expert.</li>
        </ul>
        <p>Experts are independent contractors responsible for their professional services.</p>
      </div>
    ),
  },
  {
    number: '3',
    title: 'Subscription Plans (If Applicable)',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>If FireCaseIQ offers subscription plans:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Users may cancel subscriptions at any time.</li>
          <li>
            Cancellation prevents future billing but does not refund charges already incurred.
          </li>
        </ul>
        <p>Refunds for subscription charges may be issued only in cases of:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>duplicate billing</li>
          <li>billing errors</li>
          <li>significant service outages</li>
        </ul>
        <p>Requests must be made within 7 days of the charge.</p>
      </div>
    ),
  },
  {
    number: '4',
    title: 'Prohibited Refund Situations',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>Refunds will not be granted for:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>dissatisfaction with conclusions or analysis in a Report</li>
          <li>disagreement with AI-generated findings</li>
          <li>failure to review the AI-Generated Report Policy before purchase</li>
          <li>user-submitted materials that are incomplete, inaccurate, or misleading</li>
          <li>results affected by limitations disclosed in the platform's AI policy</li>
        </ul>
        <p>Reports are preliminary analytical tools and not guaranteed outcomes.</p>
      </div>
    ),
  },
  {
    number: '5',
    title: 'Chargebacks',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          Before initiating a payment dispute or chargeback with a payment provider, users must
          first contact FireCaseIQ support.
        </p>
        <p>Improper chargebacks may result in:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>account suspension</li>
          <li>termination of platform access.</li>
        </ul>
      </div>
    ),
  },
  {
    number: '6',
    title: 'Requesting a Refund',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>Refund requests must be submitted by contacting:</p>
        <p>
          <a href="mailto:support@firecaseiq.com" className="text-primary hover:underline">
            support@firecaseiq.com
          </a>
        </p>
        <p>Please include:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>account email</li>
          <li>transaction date</li>
          <li>description of the issue.</li>
        </ul>
        <p>Refund requests are typically reviewed within 5–10 business days.</p>
      </div>
    ),
  },
  {
    number: '7',
    title: 'Discretionary Refunds',
    content: (
      <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
        <p>
          FireCaseIQ may issue refunds on a case-by-case basis at our sole discretion in situations
          where doing so is fair and reasonable.
        </p>
      </div>
    ),
  },
];

const AccordionItem = ({ section, isOpen, onToggle }) => (
  <div className="border border-border rounded-lg overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 bg-card hover:bg-muted/50 transition-colors text-left group"
    >
      <div className="flex items-center gap-3">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
          {section.number}
        </span>
        <span className="font-semibold text-foreground text-sm">{section.title}</span>
      </div>
      <Icon
        name={isOpen ? 'ChevronUp' : 'ChevronDown'}
        size={18}
        className="text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 ml-3"
      />
    </button>
    {isOpen && (
      <div className="px-5 py-4 bg-background border-t border-border">
        {section.content}
      </div>
    )}
  </div>
);

const RefundPolicyPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center">
            <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
          </button>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            Back
          </button>
        </div>
      </header>

      {/* Page content */}
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10 space-y-8">
        {/* Title block */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Refund Policy</h1>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium">Effective Date:</span> February 15, 2026
            &nbsp;&nbsp;|&nbsp;&nbsp;
            <span className="font-medium">Last Updated:</span> February 27, 2026
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed pt-1">
            This Refund Policy explains when refunds may be issued for purchases made through
            FireCaseIQ. By purchasing any Service through FireCaseIQ, you agree to this policy.
            FireCaseIQ is operated by Filmology LLC d/b/a Grunt Logic ("Grunt Logic,"
            "FireCaseIQ," "we," "us," or "our").
          </p>
        </div>

        {/* Accordion sections */}
        <div className="space-y-3">
          {sections.map((section, i) => (
            <AccordionItem
              key={section.number}
              section={section}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default RefundPolicyPage;
